import type { ModelUsageBreakdown } from "@/lib/types";

import { formatTokens } from "./formatters";

// ---------------------------------------------------------------------------
// Rate cards — $ per 1M tokens (prompts ≤ 200K context)
// ---------------------------------------------------------------------------

type RateCard = {
  label: string;
  input: number;
  cacheRead: number;
  cacheWrite: number;
  output: number;
  reasoning: number; // same as output unless noted
};

const RATE_CARDS: RateCard[] = [
  {
    label: "Claude Opus 4.6",
    input: 5.0,
    cacheRead: 0.5,
    cacheWrite: 6.25,
    output: 25.0,
    reasoning: 25.0,
  },
  {
    label: "GPT-5.2",
    input: 1.75,
    cacheRead: 0.175,
    cacheWrite: 0, // OpenAI doesn't charge for cache writes
    output: 14.0,
    reasoning: 14.0,
  },
  {
    label: "Kimi K2.5",
    input: 0.6,
    cacheRead: 0.1,
    cacheWrite: 0, // no separate cache write charge
    output: 3.0,
    reasoning: 3.0,
  },
];

function estimateCost(
  card: RateCard,
  tokens: {
    input: number;
    output: number;
    reasoning: number;
    cacheRead: number;
    cacheWrite: number;
  },
): number {
  const M = 1_000_000;
  return (
    (tokens.input / M) * card.input +
    (tokens.output / M) * card.output +
    (tokens.reasoning / M) * card.reasoning +
    (tokens.cacheRead / M) * card.cacheRead +
    (tokens.cacheWrite / M) * card.cacheWrite
  );
}

function formatUsd(value: number): string {
  if (value < 0.01) return "<$0.01";
  if (value < 1) return `$${value.toFixed(2)}`;
  if (value < 100) return `$${value.toFixed(2)}`;
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PricingComparison({ byModel }: { byModel: ModelUsageBreakdown[] }) {
  // Sum all token types across models
  const totals = byModel.reduce(
    (acc, row) => ({
      input: acc.input + row.inputTokens,
      output: acc.output + row.outputTokens,
      reasoning: acc.reasoning + row.reasoningTokens,
      cacheRead: acc.cacheRead + row.cacheReadTokens,
      cacheWrite: acc.cacheWrite + row.cacheWriteTokens,
    }),
    { input: 0, output: 0, reasoning: 0, cacheRead: 0, cacheWrite: 0 },
  );

  const totalAllTokens =
    totals.input + totals.output + totals.reasoning + totals.cacheRead + totals.cacheWrite;

  if (totalAllTokens === 0) {
    return <p className="py-4 text-center text-xs text-cyber-muted/60">No token data available.</p>;
  }

  const estimates = RATE_CARDS.map((card) => ({
    ...card,
    cost: estimateCost(card, totals),
  }));

  const minCost = Math.min(...estimates.map((e) => e.cost));
  const maxCost = Math.max(...estimates.map((e) => e.cost));

  return (
    <div className="space-y-3">
      {/* Token totals summary */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-wide text-cyber-muted/70">
        <span>
          Input: <span className="text-cyber-text">{formatTokens(totals.input)}</span>
        </span>
        <span>
          Output: <span className="text-cyber-cyan">{formatTokens(totals.output)}</span>
        </span>
        <span>
          Cache read: <span className="text-cyber-yellow">{formatTokens(totals.cacheRead)}</span>
        </span>
        <span>
          Cache write: <span className="text-cyber-magenta">{formatTokens(totals.cacheWrite)}</span>
        </span>
        {totals.reasoning > 0 && (
          <span>
            Reasoning: <span className="text-cyber-green">{formatTokens(totals.reasoning)}</span>
          </span>
        )}
      </div>

      {/* Price comparison rows */}
      <div className="space-y-2">
        {estimates.map((est) => {
          const barWidth = maxCost > 0 ? (est.cost / maxCost) * 100 : 0;
          const isCheapest = est.cost === minCost && estimates.length > 1;

          return (
            <div key={est.label}>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-cyber-text">
                  {est.label}
                  {isCheapest && (
                    <span className="ml-1.5 text-[10px] text-cyber-green">cheapest</span>
                  )}
                </span>
                <span className="font-mono text-sm text-cyber-text">{formatUsd(est.cost)}</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-cyber-surface/50">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: isCheapest
                      ? "rgb(var(--cyber-green))"
                      : "rgb(var(--cyber-cyan))",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <p className="text-[9px] leading-tight text-cyber-muted/50">
        Estimated API cost if paying per-token. Rates: Opus 4.6 (≤200K ctx), GPT-5.2 standard, Kimi
        K2.5. Our actual cost is $0 (subscription).
      </p>
    </div>
  );
}
